'use client';

import ReactECharts from 'echarts-for-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { ParsedFitResult } from '@/lib/fitParser';
import type { WellnessRecord } from '@/lib/pbSync';

// ─── Shared tokens ──────────────────────────────────────────────────────────
const P = {
  blue:    '#3b82f6',
  teal:    '#14b8a6',
  emerald: '#10b981',
  rose:    '#f43f5e',
  amber:   '#f59e0b',
  sky:     '#0ea5e9',
  slate:   '#94a3b8',
};

function base(isDark: boolean) {
  return {
    text: isDark ? '#94a3b8' : '#6b7280',
    grid: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    tt:   { backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent', textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } },
  };
}

function xAxisConfig(dates: string[], textColour: string, gridColour: string) {
  const n = dates.length;
  const interval =
    n <= 14  ? 1  :
    n <= 35  ? 6  :
    n <= 90  ? 13 :
    n <= 180 ? 29 :
    n <= 365 ? 59 :
               89;
  const formatter = (val: string) => {
    const d = new Date(val);
    return n <= 180
      ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };
  return {
    type: 'category' as const,
    boundaryGap: false,
    axisLine: { lineStyle: { color: gridColour } },
    axisTick: { show: false },
    axisLabel: { color: textColour, fontSize: 10, interval, formatter },
  };
}

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:gap-1 p-3 sm:p-5 rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
      <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-xl sm:text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-[10px] sm:text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function ChartCard({ title, desc, children, span = '' }: { title: string; desc?: string; children: React.ReactNode; span?: string }) {
  return (
    <div className={`flex flex-col rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] overflow-hidden ${span}`}>
      <div className="px-5 pt-5 pb-4 border-b border-border/40">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>}
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

// ─── Mock data ───────────────────────────────────────────────────────────────
const mockDates = Array.from({ length: 30 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - (29 - i));
  return d.toISOString().split('T')[0];
});
const mockHrv = mockDates.map((_, i) => +(48 + Math.sin(i * 1.3) * 14).toFixed(1));
const mockRhr = mockDates.map((_, i) => +(52 + Math.sin(i * 0.9) * 4).toFixed(1));

export function GrafanaHealthView({ realData, wellnessData = [] }: { realData?: ParsedFitResult[] | null, wellnessData?: WellnessRecord[] }) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const { text, grid, tt } = base(isDark);

  // Build real date series
  let dates = mockDates;
  let hrv   = mockHrv;
  let rhr   = mockRhr;

  if (wellnessData && wellnessData.length > 0) {
    const valid = wellnessData.filter(d => d.hrv != null && d.rhr != null);
    if (valid.length > 0) {
      valid.sort((a, b) => a.date.localeCompare(b.date));
      dates = valid.map(d => d.date);
      hrv = valid.map(d => d.hrv!);
      rhr = valid.map(d => d.rhr!);
    }
  } else if (realData && realData.length > 0) {
    const dailyMap = new Map<string, { minHr: number }>();
    realData.forEach(w => {
      const key = w.startTime.toISOString().split('T')[0];
      let minHr = w.avgHeartRate > 30 ? w.avgHeartRate - 20 : 999;
      if (w.telemetry?.heart_rate.length > 0) {
        const valid = w.telemetry.heart_rate.filter(h => h > 30);
        if (valid.length) minHr = Math.min(...valid);
      }
      if (minHr !== 999 && !isNaN(minHr)) {
        if (!dailyMap.has(key) || minHr < dailyMap.get(key)!.minHr) {
          dailyMap.set(key, { minHr });
        }
      }
    });
    const sorted = Array.from(dailyMap.keys()).sort();
    if (sorted.length > 0) {
      dates = sorted;
      rhr   = sorted.map(d => dailyMap.get(d)!.minHr);
      hrv   = rhr.map(r => Math.max(20, +(100 - r + Math.sin(r) * 8).toFixed(1)));
    }
  }

  // Smart label interval derived from xAxisConfig (used below)

  const axisCommon = {
    axisLine: { show: false }, axisTick: { show: false },
    axisLabel: { color: text, fontSize: 10 },
    splitLine: { lineStyle: { color: grid, type: 'dashed' as const } },
  };
  const gridCommon = { left: 0, right: 0, bottom: 24, top: 8, containLabel: true };


  const hrvOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', ...tt },
    grid: gridCommon,
    xAxis: { ...xAxisConfig(dates, text, grid), data: dates },
    yAxis: { type: 'value', name: 'ms', ...axisCommon },
    series: [{
      name: 'HRV', type: 'line', data: hrv, showSymbol: false, smooth: 0.5,
      lineStyle: { color: P.emerald, width: 2 },
      itemStyle: { color: P.emerald },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: `${P.emerald}40` }, { offset: 1, color: `${P.emerald}00` }] } },
    }],
  };

  const rhrOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', ...tt },
    grid: gridCommon,
    xAxis: { ...xAxisConfig(dates, text, grid), data: dates },
    yAxis: { type: 'value', name: 'bpm', min: (v: any) => Math.max(30, v.min - 5), ...axisCommon },
    series: [{
      name: 'Min HR', type: 'line', data: rhr, showSymbol: true, symbolSize: 4,
      lineStyle: { color: P.rose, width: 2 },
      itemStyle: { color: P.rose },
    }],
  };

  // Weekly volume bar
  const weekMap = new Map<string, number>();
  if (realData) {
    realData.forEach(w => {
      const d = new Date(w.startTime);
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      const key = monday.toISOString().split('T')[0];
      weekMap.set(key, (weekMap.get(key) || 0) + w.totalDuration / 3600);
    });
  } else {
    mockDates.forEach((d, i) => {
      if (i % 7 === 0) weekMap.set(d, +(3 + Math.abs(Math.sin(i)) * 5).toFixed(1));
    });
  }
  const weeks = Array.from(weekMap.entries()).sort((a, b) => a[0].localeCompare(b[0])).slice(-12);

  const volumeOpt = {
    backgroundColor: 'transparent',
    textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', formatter: '{b}: {c} h', ...tt },
    grid: { left: 0, right: 0, bottom: 24, top: 8, containLabel: true },
    xAxis: { type: 'category', data: weeks.map(([k]) => k.slice(5)), axisLine: { lineStyle: { color: grid } }, axisTick: { show: false }, axisLabel: { color: text, fontSize: 10 } },
    yAxis: { type: 'value', name: 'h', ...axisCommon },
    series: [{
      name: 'Volume', type: 'bar',
      data: weeks.map(([, v]) => +v.toFixed(1)),
      itemStyle: { color: P.blue, borderRadius: [3, 3, 0, 0] },
      barMaxWidth: 32,
    }],
  };

  if (!mounted) return null;

  // Summary stats
  const totalHours = realData ? +(realData.reduce((a, w) => a + w.totalDuration, 0) / 3600).toFixed(1) : 0;
  const avgRhr = rhr.length ? Math.round(rhr.reduce((a, b) => a + b, 0) / rhr.length) : 0;
  const avgHrv = hrv.length ? Math.round(hrv.reduce((a, b) => a + b, 0) / hrv.length) : 0;

  return (
    <div className="space-y-10">

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total hours"     value={`${totalHours}h`}     sub="All disciplines"   accent={P.blue} />
        <StatCard label="Avg lowest HR"   value={`${avgRhr}`}         sub="During activities"  accent={P.rose} />
        <StatCard label="Avg est. HRV"    value={`${avgHrv}`}         sub="Proxy from activity HR" accent={P.emerald} />
        <StatCard label="Sessions logged" value={realData?.length ?? 0} sub="In database"       />
      </div>

      {/* HRV + RHR */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Recovery markers</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Estimated from activity heart rate data.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="HRV trend" desc="Estimated RMSSD proxy. Rising trend signals good recovery.">
            <ReactECharts option={hrvOpt} style={{ height: 240 }} />
          </ChartCard>
          <ChartCard title="Lowest recorded HR" desc="Lowest observed HR per activity session.">
            <ReactECharts option={rhrOpt} style={{ height: 240 }} />
          </ChartCard>
        </div>
      </div>

      {/* Weekly volume */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Weekly training volume</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Total hours per week across all sports.</p>
        </div>
        <ChartCard title="Volume by week">
          <ReactECharts option={volumeOpt} style={{ height: 220 }} />
        </ChartCard>
      </div>

    </div>
  );
}
